import { DatePipe, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { LogsService } from '@services/logs';
import { ResponsiveService } from '@services/responsive';
import { ButtonComponent } from '@shared/components/button';
import { Day } from '@shared/components/date-picker/model';
import { AvailableSlots } from '@shared/components/date-picker/model/available-slots';
import { IsDateIsAvailablePipe, IsDateSelectedPipe } from '@shared/components/date-picker/pipes';
import { AppointmentsSlotsService } from '@shared/components/date-picker/services';
import { IconsDirective } from '@shared/directives';
import { Schedule } from '@shared/models';
import { ArrayLengthPipe, SplitArrayByPipe } from '@shared/pipes';
import { WeekDay } from '@shared/types';
import { Subject, takeUntil } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-date-picker',
  styleUrl: './date-picker.component.scss',
  templateUrl: './date-picker.component.html',
  imports: [
    ButtonComponent,
    DatePipe,
    IconsDirective,
    SplitArrayByPipe,
    TitleCasePipe,
    IsDateIsAvailablePipe,
    IsDateSelectedPipe,
    ArrayLengthPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerComponent implements OnInit, OnDestroy {
  readonly storeUuid = input.required<string>();
  readonly storePhoneNumber = input<string>();
  readonly availableDays = input.required<WeekDay[]>();
  readonly date = output<{
    day: Date;
    schedule: Schedule;
  }>();
  readonly daysElementsRef = viewChild.required<ElementRef<HTMLElement>>('daysElementsRef');
  readonly schedulesElementRef = viewChild.required<ElementRef<HTMLElement>>('schedulesElementRef');
  availableSlots = signal<AvailableSlots>(null!);
  availableSlotsLoaded = signal(false);
  dateArray = signal<Date[]>(null!);
  currentScrollDays = signal(0);
  currentScrollSchedules = signal(0);
  firstWeekDay = signal(0);
  dateSelected = signal<Date>(null!);
  protected readonly DISPLAYED_WEEKS = 6;
  readonly #breakpoint = inject(ResponsiveService).breakpoints;
  readonly #appointmentsSlotsService = inject(AppointmentsSlotsService);
  readonly #logsService = inject(LogsService);
  #destroy$: Subject<boolean> = new Subject<boolean>();
  private readonly SUNDAY: WeekDay = 6;
  protected daysPerPage = 7;
  protected includeSunday = true;

  ngOnInit(): void {
    if (!this.storeUuid()) {
      throw Error(`The "storeUuid" property is mandatory.`);
    }
    this.includeSunday = this.availableDays().includes(this.SUNDAY);
    this.daysPerPage = this.includeSunday ? 7 : 6;
    this.buildCalendar();
  }

  ngOnDestroy(): void {
    this.#destroy$.next(true);
    this.#destroy$.complete();
  }

  onNext() {
    this.firstWeekDay.set(this.firstWeekDay() + 1);
    const dateLength = this.splitDateArray(this.dateArray()).length;
    const calendarLength = this.splitDateArray(this.availableSlots().slots).length;
    if (this.currentScrollDays() < this.daysElementsRef().nativeElement.clientWidth * (dateLength - 1)) {
      this.currentScrollDays.set(this.currentScrollDays() + this.daysElementsRef().nativeElement.clientWidth);
    }

    const scheduleSize =
      this.#breakpoint() === 'mobile'
        ? this.daysPerPage * this.schedulesElementRef().nativeElement.clientWidth
        : this.schedulesElementRef().nativeElement.clientWidth;
    if (this.currentScrollSchedules() < scheduleSize * (calendarLength - 1)) {
      this.currentScrollSchedules.set(this.currentScrollSchedules() + scheduleSize);
    }

    this.schedulesElementRef().nativeElement.scrollTo({ left: this.currentScrollSchedules() });
    this.daysElementsRef().nativeElement.scrollTo({ left: this.currentScrollDays() });
    this.selectedDateByIndex(this.firstWeekDay() * this.daysPerPage);
  }

  onPrev() {
    this.firstWeekDay.set(this.firstWeekDay() - 1);
    if (this.currentScrollDays() > 0) {
      this.currentScrollDays.set(this.currentScrollDays() - this.daysElementsRef().nativeElement.clientWidth);
    }

    const scheduleSize =
      this.#breakpoint() === 'mobile'
        ? this.daysPerPage * this.schedulesElementRef().nativeElement.clientWidth
        : this.schedulesElementRef().nativeElement.clientWidth;
    if (this.currentScrollSchedules() > 0) {
      this.currentScrollSchedules.set(this.currentScrollSchedules() - scheduleSize);
    }

    this.schedulesElementRef().nativeElement.scrollTo({ left: this.currentScrollSchedules() });
    this.daysElementsRef().nativeElement.scrollTo({ left: this.currentScrollDays() });
    this.selectedDateByIndex(this.firstWeekDay() * this.daysPerPage);
  }

  splitDateArray<T>(array: T[]): T[][] {
    const newArray: T[][] = [];
    array.forEach((_, index) => {
      if (index % this.daysPerPage === 0) {
        newArray.push(array.slice(index, index + this.daysPerPage));
      }
    });

    return newArray;
  }

  selectedDate(day: Date): void {
    this.dateSelected.set(day);
    if (this.#breakpoint() === 'mobile') {
      const dayIndex = this.dateArray().indexOf(day);
      this.currentScrollSchedules.set(this.schedulesElementRef().nativeElement.clientWidth * dayIndex);
      this.schedulesElementRef().nativeElement.scrollTo({ left: this.currentScrollSchedules() });
    }
  }

  selectSchedules(day: Date, schedule: Schedule): void {
    this.date.emit({ day, schedule });
  }

  private buildCalendar(): void {
    this.dateArray.set(this.generateDateArray());

    const start = new Date();
    const end = new Date(start);
    end.setDate(end.getDate() + this.DISPLAYED_WEEKS * 7 - 1);

    this.#appointmentsSlotsService
      .getSlots(this.storeUuid(), start.toISOString().split('T')[0]!, end.toISOString().split('T')[0]!)
      .pipe(
        takeUntil(this.#destroy$),
        tap((availableSlots) => {
          if (availableSlots.status !== 'OK' && !availableSlots.message?.title && !availableSlots.message?.bodyHtml) {
            this.availableSlots.set(this.buildUnavailableSlots());
            this.availableSlotsLoaded.set(true);
            return;
          }

          const availableDays = this.availableDays();

          const appointments = availableSlots.slots.filter((slot) => {
            const jsDay = new Date(slot.date).getDay();
            const normalizedDay = ((jsDay + 6) % 7) as WeekDay;
            return availableDays.includes(normalizedDay);
          });

          this.availableSlots.set({
            ...availableSlots,
            slots: this.dateArray().map((date, index) => {
              const appointmentsFound = appointments.find((day) => {
                const dateAppointment = new Date(new Date(day.date).setHours(0, 0, 0, 0));
                return dateAppointment.getTime() === date.getTime();
              });
              return this.getDay(date, appointmentsFound, index);
            }),
          });
          this.availableSlotsLoaded.set(true);
          if (this.#breakpoint() === 'mobile') {
            setTimeout(() => {
              this.selectedDate(this.dateSelected());
            }, 0);
          }
        })
      )
      .subscribe({
        error: (err: unknown) => {
          this.#logsService.error('DatePickerComponent', 'Appointment slots fetch failed', err);
          this.availableSlots.set(this.buildUnavailableSlots());
          this.availableSlotsLoaded.set(true);
        },
      });
  }

  private buildUnavailableSlots(): AvailableSlots {
    const phone = this.storePhoneNumber();
    const intro = 'En attendant de pouvoir prendre rendez-vous en ligne, vous pouvez contacter la boutique au';
    const bodyText = phone ? `${intro} ${phone}.` : 'Merci de réessayer un peu plus tard.';
    // The phone can come from the `phone` query param, so it is escaped before entering the HTML.
    const htmlPhone = phone?.replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
    const bodyHtml = htmlPhone ? `${intro} <a href="tel:${htmlPhone}">${htmlPhone}</a>.` : bodyText;

    return {
      status: 'KO',
      message: { title: 'Maintenance en cours', bodyText, bodyHtml },
      cta: null,
      slots: [],
    };
  }

  private generateDateArray(): Date[] {
    const currentDate = new Date();
    const currentDayOfWeek = currentDate.getDay(); // 0 (dimanche) à 6 (samedi)
    const daysToFirstDayOfWeek = currentDayOfWeek === 1 ? 0 : currentDayOfWeek === 0 ? -6 : 1 - currentDayOfWeek;

    const startDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate() + daysToFirstDayOfWeek
    );
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + this.DISPLAYED_WEEKS * 7 - 1);

    const dateArray: Date[] = [];
    for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
      if (this.includeSunday || date.getDay() !== 0) {
        dateArray.push(new Date(date));
      }
    }

    return dateArray;
  }

  private getDay(date: Date, appointments: Day | undefined, index: number): Day {
    if (appointments?.schedules && appointments.schedules.length > 0 && !this.dateSelected()) {
      this.dateSelected.set(date);
      setTimeout(() => {
        const pageIndex = Math.floor(index / this.daysPerPage);
        while (this.firstWeekDay() < pageIndex) {
          this.onNext();
        }
      }, 1);
    }
    return {
      date,
      schedules: appointments?.schedules ?? [],
    };
  }

  private selectedDateByIndex(index: number): void {
    for (let i = index; i < this.availableSlots().slots.length; i++) {
      if (this.availableSlots().slots[i]!.schedules[0] && this.availableSlots().slots[i]!.schedules[0]!.start) {
        this.selectedDate(this.availableSlots().slots[i]!.date);
        break;
      }
    }
  }
}
